<template>
	<div class="basic-info">
		<el-form :model="form" ref="form" label-width="88px" size="mini" :rules="addRules"
			:disabled="formDisabled || mode === 'view'">
			<el-row :gutter="24">
				<template v-for="field in dynamicFields">
					<el-col :key="field.fieldCode" :xs="24" :sm="24" :md="12" :lg="field.colSpan || 24"
						:xl="field.colSpan || 24" v-if="field.isShow">
						<el-form-item :label="field.fieldName + '：'" :prop="field.fieldCode"
							:rules="getFieldRules(field)" class="form-item-compact">
							<!-- 输入框 -->
							<el-input v-if="field.inputBoxType === 'input'" v-model="form[field.fieldCode]"
								:placeholder="getFieldPlaceholder(field)" @blur="updateForm"
								@input="handleFieldInput(field)" clearable class="compact-input"></el-input>

							<!-- 下拉选择框 -->
							<el-select v-else-if="field.inputBoxType === 'selected'" v-model="form[field.fieldCode]"
								:placeholder="getFieldPlaceholder(field)" @change="handleFieldChange(field)" filterable
								clearable class="compact-select">
								<el-option v-for="option in getFieldOptions(field)" :key="getOptionKey(option)"
									:label="option.label" :value="option.id"></el-option>
							</el-select>

							<!-- 复选框 -->
							<template v-else-if="field.inputBoxType === 'checked'">
								<el-checkbox v-model="form[field.fieldCode]" @change="handleFieldChange(field)"
									class="compact-checkbox">
									{{ field.fieldName }}
								</el-checkbox>
							</template>

							<!-- 文本域 -->
							<el-input v-else-if="field.inputBoxType === 'textarea'" type="textarea"
								:rows="field.textareaRows || 24" v-model="form[field.fieldCode]" @blur="updateForm"
								:autosize="{ minRows: 2, maxRows: 6 }"
								:placeholder="getFieldPlaceholder(field)" class="compact-textarea"></el-input>

							<!-- 级联选择器 -->
							<el-cascader v-else-if="field.inputBoxType === 'cascader'" v-model="form[field.fieldCode]"
								:options="getCascaderOptions(field)" :props="getCascaderProps(field)"
								:placeholder="getFieldPlaceholder(field)" @change="handleFieldChange(field)" clearable
								filterable class="compact-cascader"></el-cascader>

							<!-- 默认输入框 -->
							<el-input v-else v-model="form[field.fieldCode]" :placeholder="getFieldPlaceholder(field)"
								@blur="updateForm" @input="handleFieldInput(field)" clearable
								class="compact-input"></el-input>
						</el-form-item>
					</el-col>
				</template>
			</el-row>
		</el-form>
	</div>
</template>

<script>
	export default {
		props: {
			form: {
				type: Object,
				required: true
			},
			formDisabled: {
				type: Boolean,
				default: false
			},
			mode: {
				type: String,
				default: 'add'
			}
		},
		data() {
			return {
				dynamicFields: [],
				fieldOptionsMap: new Map(),
				fieldRulesMap: new Map(),
				fieldCodeOptionsMap: new Map(),
				addRules: {},

				// 选项数据映射表
				optionDataMap: {
					textTypeOptions: [{
							id: 1,
							label: '类型1'
						},
						{
							id: 2,
							label: '类型2'
						}
					]
				},

				// 字段配置处理状态
				fieldConfigProcessed: false,
				pendingFieldConfig: null,
			};
		},
		watch: {
			dynamicFields: {
				deep: true,
				handler() {
					this.$nextTick(() => {
						this.rebuildFieldMaps();
					});
				}
			}
		},
		mounted() {
			this.$nextTick(() => {
				if (this.pendingFieldConfig) {
					this.processPendingFieldConfig();
				} else {
					this.setupDefaultFields();
				}
			});
		},
		methods: {
			// 获取选项的唯一key
			getOptionKey(option) {
				return option.id || option.value || Math.random().toString(36).substr(2, 9);
			},

			// 字段输入处理
			handleFieldInput(field) {
				this.updateForm();
			},

			// 字段变化处理
			handleFieldChange(field) {
				this.updateForm();
			},

			// 获取字段占位符
			getFieldPlaceholder(field) {
				const placeholderMap = {
					'input': `请输入${field.fieldName}`,
					'selected': `请选择${field.fieldName}`,
					'textarea': `请输入${field.fieldName}`,
					'cascader': `请选择${field.fieldName}`,
					'checked': ''
				};

				return field.placeholder || placeholderMap[field.inputBoxType] || `请输入${field.fieldName}`;
			},

			// 接收字段配置
			setFieldConfig(fieldConfig) {
				if (fieldConfig && fieldConfig.length > 0) {
					console.log('F4组件接收到字段配置:', fieldConfig);

					if (this.fieldConfigProcessed) {
						this.processFieldConfigImmediately(fieldConfig);
					} else {
						this.pendingFieldConfig = fieldConfig;
						console.log('字段配置已保存，等待初始化完成后处理');
					}
				} else {
					console.warn('F4组件：未接收到有效的字段配置');
					this.setupDefaultFields();
				}
			},

			processPendingFieldConfig() {
				if (this.pendingFieldConfig) {
					this.processFieldConfigImmediately(this.pendingFieldConfig);
					this.pendingFieldConfig = null;
				}
			},

			processFieldConfigImmediately(fieldConfig) {
				try {
					this.processFieldData(fieldConfig);
					this.fieldConfigProcessed = true;
					this.$message.success('F4组件字段配置加载成功');
				} catch (error) {
					console.error('处理字段配置失败:', error);
					this.$message.error('字段配置处理失败');
					this.setupDefaultFields();
				}
			},
			// 清除表单验证
			clearFormValidation() {
				if (this.$refs.form) {
					this.$refs.form.clearValidate();
				}
			},
			processFieldData(fieldData) {
				try {
					const visibleFields = fieldData.filter(field =>
						field.isShow === 1 || field.isMustFill === 1 || field.isMustChoose === 1
					);

					this.dynamicFields = visibleFields.map(field => ({
						fieldCode: field.fieldCode,
						fieldName: field.fieldName || this.getDefaultFieldName(field.fieldCode),
						inputBoxType: field.inputBoxType || 'input',
						isShow: true,
						isMustFill: field.isMustFill || 0,
						isMustChoose: field.isMustChoose || 0,
						placeholder: field.placeholder || '',
						options: field.options || [],
						rules: field.rules || [],
						colSpan: field.colSpan || 24,
						fieldCodeOption: field.fieldCodeOption || field.fieldCode,
						textareaRows: field.textareaRows || 24,
						cascaderProps: field.cascaderProps || this.getDefaultCascaderProps(),
						cascaderOptions: field.cascaderOptions || []
					}));

					this.buildFieldOptionsMap();
					this.buildFieldRulesMap();
					this.buildFieldCodeOptionsMap();

					console.log('F4组件处理后的动态字段:', this.dynamicFields);
					this.fieldConfigProcessed = true;
					this.$forceUpdate();
				} catch (error) {
					console.error('处理字段数据失败:', error);
					throw error;
				}
			},

			buildFieldOptionsMap() {
				this.fieldOptionsMap.clear();
				this.dynamicFields.forEach(field => {
					if (field.options && field.options.length > 0) {
						this.fieldOptionsMap.set(field.fieldCode, field.options);
					}
				});
				console.log('F4字段选项映射构建完成:', this.fieldOptionsMap);
			},

			buildFieldRulesMap() {
				this.fieldRulesMap.clear();
				this.dynamicFields.forEach(field => {
					if (field.rules && field.rules.length > 0) {
						this.fieldRulesMap.set(field.fieldCode, field.rules);
					}
				});
				console.log('F4字段验证规则映射构建完成:', this.fieldRulesMap);
			},

			buildFieldCodeOptionsMap() {
				this.fieldCodeOptionsMap.clear();
				this.dynamicFields.forEach(field => {
					if (field.inputBoxType === 'selected') {
						const optionsArray = this.getOptionsByFieldCodeOption(field.fieldCodeOption);
						if (optionsArray && optionsArray.length > 0) {
							this.fieldCodeOptionsMap.set(field.fieldCode, optionsArray);
						}
					}
				});
				console.log('F4 fieldCode选项映射构建完成:', this.fieldCodeOptionsMap);
			},

			rebuildFieldMaps() {
				this.buildFieldOptionsMap();
				this.buildFieldRulesMap();
				this.buildFieldCodeOptionsMap();
			},

			getOptionsByFieldCodeOption(fieldCodeOption) {
				const optionsArray = this.optionDataMap[fieldCodeOption];
				console.log(`F4查找fieldCodeOption: ${fieldCodeOption}, 找到选项数组:`, optionsArray);
				return optionsArray || [];
			},

			getFieldOptions(field) {
				if (this.fieldCodeOptionsMap.has(field.fieldCode)) {
					const options = this.fieldCodeOptionsMap.get(field.fieldCode);
					console.log(`F4从fieldCodeOptionsMap获取字段 ${field.fieldCode} 的选项:`, options);
					return options;
				}

				if (field.options && field.options.length > 0) {
					console.log(`F4使用字段自带的options获取字段 ${field.fieldCode} 的选项:`, field.options);
					return field.options;
				}

				const defaultOptions = this.getDefaultFieldOptions(field.fieldCode);
				return defaultOptions;
			},

			getCascaderOptions(field) {
				if (field.inputBoxType === 'cascader') {
					return field.cascaderOptions || [];
				}
				return [];
			},

			getCascaderProps(field) {
				if (field.inputBoxType === 'cascader') {
					return field.cascaderProps || this.getDefaultCascaderProps();
				}
				return {};
			},

			getDefaultCascaderProps() {
				return {
					value: 'id',
					label: 'label',
					children: 'children',
					checkStrictly: true,
					emitPath: true,
					expandTrigger: 'hover'
				};
			},

			getDefaultFieldName(fieldCode) {
				const fieldNameMap = {
					'phamAction': '药理作用',
					'efccAtd': '适应症',
					'usedMtd': '用法用量',
					'phas': '药代动力学',
					'defs': '不良反应',
					'mnan': '注意事项',
					'tabo': '禁忌说明',
					'drugInteractions': '药物相互作用',
					'overdose': '药物过量',
					'clinicalPharmacology': '临床药理',
					'storageConditions': '贮藏条件',
					'packagingSpecifications': '包装规格',
					'validityPeriod': '有效期',
					'approvalNumber': '批准文号',
					'manufacturer': '生产企业'
				};

				return fieldNameMap[fieldCode] || fieldCode;
			},

			getDefaultFieldOptions(fieldCode) {
				const optionsMap = {
					// F4组件主要是文本域，这里可以添加一些选择框的默认选项
				};

				return optionsMap[fieldCode] || [];
			},

			getFieldRules(field) {
				const baseRules = this.fieldRulesMap.get(field.fieldCode) || [];

				if (field.isMustFill === 1 || field.isMustChoose === 1) {
					const requiredRule = {
						required: true,
						message: `请输入${field.fieldName}`,
						trigger: 'blur'
					};

					if (field.inputBoxType === 'selected') {
						requiredRule.message = `请选择${field.fieldName}`;
						requiredRule.trigger = 'change';
					}

					if (field.inputBoxType === 'cascader') {
						requiredRule.message = `请选择${field.fieldName}`;
						requiredRule.trigger = 'change';
					}

					if (field.inputBoxType === 'checked') {
						requiredRule.message = `请选择${field.fieldName}`;
						requiredRule.trigger = 'change';
					}

					return [requiredRule, ...baseRules];
				}

				return baseRules;
			},

			setupDefaultFields() {
				const defaultFields = [{
						fieldCode: 'phamAction',
						fieldName: '药理作用',
						inputBoxType: 'textarea',
						textareaRows: 4,
						isShow: 1
					},
					{
						fieldCode: 'efccAtd',
						fieldName: '适应症',
						inputBoxType: 'textarea',
						textareaRows: 4,
						isShow: 1
					},
					{
						fieldCode: 'usedMtd',
						fieldName: '用法用量',
						inputBoxType: 'textarea',
						textareaRows: 4,
						isShow: 1
					},
					{
						fieldCode: 'phas',
						fieldName: '药代动力学',
						inputBoxType: 'textarea',
						textareaRows: 3,
						isShow: 1
					},
					{
						fieldCode: 'defs',
						fieldName: '不良反应',
						inputBoxType: 'textarea',
						textareaRows: 4,
						isShow: 1
					},
					{
						fieldCode: 'mnan',
						fieldName: '注意事项',
						inputBoxType: 'textarea',
						textareaRows: 4,
						isShow: 1
					},
					{
						fieldCode: 'tabo',
						fieldName: '禁忌说明',
						inputBoxType: 'textarea',
						textareaRows: 3,
						isShow: 1
					},
					{
						fieldCode: 'drugInteractions',
						fieldName: '药物相互作用',
						inputBoxType: 'textarea',
						textareaRows: 3,
						isShow: 1
					},
					{
						fieldCode: 'overdose',
						fieldName: '药物过量',
						inputBoxType: 'textarea',
						textareaRows: 3,
						isShow: 1
					},
					{
						fieldCode: 'clinicalPharmacology',
						fieldName: '临床药理',
						inputBoxType: 'textarea',
						textareaRows: 3,
						isShow: 1
					},
					{
						fieldCode: 'storageConditions',
						fieldName: '贮藏条件',
						inputBoxType: 'input',
						isShow: 1
					},
					{
						fieldCode: 'packagingSpecifications',
						fieldName: '包装规格',
						inputBoxType: 'input',
						isShow: 1
					},
					{
						fieldCode: 'validityPeriod',
						fieldName: '有效期',
						inputBoxType: 'input',
						isShow: 1
					},
					{
						fieldCode: 'approvalNumber',
						fieldName: '批准文号',
						inputBoxType: 'input',
						isShow: 1
					},
					{
						fieldCode: 'manufacturer',
						fieldName: '生产企业',
						inputBoxType: 'input',
						isShow: 1
					}
				];

				this.processFieldData(defaultFields);
			},

			updateForm() {
				this.$emit('update', this.form);
			},

			resetForm() {
				Object.keys(this.form).forEach(key => {
					if (typeof this.form[key] === 'boolean') {
						this.form[key] = false;
					} else if (typeof this.form[key] === 'number') {
						this.form[key] = 0;
					} else {
						this.form[key] = '';
					}
				});

				if (this.$refs.form) {
					this.$refs.form.clearValidate();
				}
			},

			// 新增：设置选项数据的方法
			setOptionData(optionName, optionData) {
				if (optionName && Array.isArray(optionData)) {
					this.optionDataMap[optionName] = optionData;
					this.rebuildFieldMaps();
				}
			},

			// 新增：处理级联选择器变化
			handleCascaderChange(field, value) {
				console.log(`级联选择器 ${field.fieldCode} 值变化:`, value);
				this.updateForm();
			}
		}
	};
</script>

<style lang="less" scoped>
	.basic-info {
		padding: 12px;
		background: #fff;
		border-radius: 4px;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
		font-family: "Microsoft YaHei", Arial, sans-serif;

		/deep/ .el-input--mini .el-input__inner {
			height: 26px;
		}

		.el-row {
			margin: 0 -8px;

			.el-col {
				padding: 0 8px;
				margin-bottom: 12px;
			}
		}

		.form-item-compact {
			margin-bottom: 8px;

			.el-form-item__label {
				font-size: 12px;
				color: #606266;
				font-weight: 500;
				line-height: 28px;
				padding: 0;
				text-align: right;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
			}

			.el-form-item__content {
				line-height: 28px;
				position: relative;
				font-size: 12px;
			}
		}

		.compact-input {
			.el-input__inner {
				height: 28px;
				line-height: 28px;
				font-size: 12px;
				padding: 0 8px;
				border: 1px solid #dcdfe6;
				border-radius: 2px;
				color: #606266;
				background-color: #fff;

				&:focus {
					border-color: #409eff;
					outline: 0;
					box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
				}

				&:hover {
					border-color: #c0c4cc;
				}

				&::placeholder {
					color: #c0c4cc;
					font-size: 12px;
				}
			}
		}

		.compact-select {
			width: 100%;

			.el-input__inner {
				height: 28px;
				line-height: 28px;
				font-size: 12px;
				padding: 0 8px;
				border: 1px solid #dcdfe6;
				border-radius: 2px;

				&:focus {
					border-color: #409eff;
				}
			}

			.el-select-dropdown__item {
				font-size: 12px;
				height: 28px;
				line-height: 28px;
				padding: 0 8px;
			}
		}

		.compact-cascader {
			width: 100%;

			.el-input__inner {
				height: 28px;
				line-height: 28px;
				font-size: 12px;
				padding: 0 8px;
				border: 1px solid #dcdfe6;
				border-radius: 2px;

				&:focus {
					border-color: #409eff;
				}
			}
		}

		.compact-checkbox {
			font-size: 12px;
			color: #606266;

			.el-checkbox__label {
				font-size: 12px;
				padding-left: 4px;
			}

			.el-checkbox__inner {
				width: 14px;
				height: 14px;

				&::after {
					left: 4px;
					top: 1px;
					width: 3px;
					height: 6px;
				}
			}
		}

		.compact-textarea {
			.el-textarea__inner {
				min-height: 80px;
				font-size: 12px;
				line-height: 1.5;
				padding: 6px 8px;
				border: 1px solid #dcdfe6;
				border-radius: 2px;
				resize: vertical;

				&:focus {
					border-color: #409eff;
					outline: 0;
					box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
				}

				&::placeholder {
					color: #c0c4cc;
					font-size: 12px;
				}
			}
		}

		.el-form-item.is-error {

			.compact-input .el-input__inner,
			.compact-select .el-input__inner,
			.compact-cascader .el-input__inner,
			.compact-textarea .el-textarea__inner {
				border-color: #f56c6c;
			}
		}

		.el-form-item.is-disabled {

			.compact-input .el-input__inner,
			.compact-select .el-input__inner,
			.compact-cascader .el-input__inner,
			.compact-textarea .el-textarea__inner {
				background-color: #f5f7fa;
				border-color: #e4e7ed;
				color: #c0c4cc;
				cursor: not-allowed;
			}
		}

		// 文本域字段特殊样式
		.textarea-field {
			.el-col {
				margin-bottom: 16px;
			}

			.el-form-item {
				margin-bottom: 12px;
			}

			.compact-textarea .el-textarea__inner {
				min-height: 100px;
			}
		}
	}

	@media (max-width: 768px) {
		.basic-info {
			padding: 8px;

			.el-row {
				margin: 0 -4px;

				.el-col {
					padding: 0 4px;
					margin-bottom: 8px;
				}
			}

			.form-item-compact {
				margin-bottom: 6px;
			}

			.compact-input .el-input__inner,
			.compact-select .el-input__inner,
			.compact-cascader .el-input__inner {
				height: 26px;
				line-height: 26px;
				font-size: 11px;
			}

			.compact-textarea .el-textarea__inner {
				min-height: 70px;
				font-size: 11px;
			}
		}
	}

	@media (max-width: 480px) {
		.basic-info {
			padding: 6px;

			.el-row {
				margin: 0 -2px;

				.el-col {
					padding: 0 2px;
					margin-bottom: 6px;
				}
			}

			.form-item-compact {
				margin-bottom: 4px;

				.el-form-item__label {
					font-size: 11px;
					line-height: 24px;
				}
			}

			.compact-input .el-input__inner,
			.compact-select .el-input__inner,
			.compact-cascader .el-input__inner {
				height: 24px;
				line-height: 24px;
				font-size: 11px;
				padding: 0 6px;
			}

			.compact-textarea .el-textarea__inner {
				min-height: 60px;
				font-size: 11px;
				padding: 4px 6px;
			}
		}
	}

	// 打印样式优化
	@media print {
		.basic-info {
			box-shadow: none;
			border: 1px solid #ddd;

			.compact-input .el-input__inner,
			.compact-select .el-input__inner,
			.compact-cascader .el-input__inner,
			.compact-textarea .el-textarea__inner {
				border: 1px solid #999;
				background: transparent;
			}

			.compact-textarea .el-textarea__inner {
				min-height: auto;
				height: auto;
			}
		}
	}
</style>